import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  MenuItem, CircularProgress, Avatar, Alert, Switch, Tooltip, Divider, Paper,
  InputAdornment, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add, Edit, Delete, Close, ManageAccounts, Lock, Person, Email,
  ShieldOutlined, VerifiedUser, Search, KeyOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { usersURL, usersAddURL, usersUpdateURL, usersDeleteURL } from '../BaseURL';
import { useNotify } from '../notify';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('church_token')}` });
const getUser = () => { try { return JSON.parse(localStorage.getItem('church_user')); } catch { return null; } };

const ROLE_COLORS = {
  superadmin: 'warning',
  admin:      'primary',
  accountant: 'success',
  viewer:     'default',
};

const PERMISSION_GROUPS = [
  {
    title: 'Cash Book',
    modules: [
      { key: 'cash_book',     label: 'View Cash Book',    desc: 'The unified monthly worksheet (home page)' },
      { key: 'record_income', label: 'Record Income',     desc: 'Enter income receipts' },
      { key: 'payments',      label: 'Record Payments',   desc: 'Payment vouchers & bank transfers' },
    ],
  },
  {
    title: 'Setup',
    modules: [
      { key: 'income_types',  label: 'Income Types',   desc: 'Manage income types & categories' },
      { key: 'expense_types', label: 'Expense Types',  desc: 'Manage expense types & categories' },
      { key: 'bank_accounts', label: 'Bank Accounts',  desc: 'Manage bank accounts' },
    ],
  },
  {
    title: 'Reports & Admin',
    modules: [
      { key: 'reports',  label: 'Reports',          desc: 'All accounting reports' },
      { key: 'users',    label: 'User Management',  desc: 'Add and manage other users' },
      { key: 'settings', label: 'Church Settings',  desc: 'Edit church info & logo' },
    ],
  },
];

const DEFAULT_PERMS_BY_ROLE = {
  superadmin: {
    income_types: true, expense_types: true, bank_accounts: true,
    record_income: true, payments: true, cash_book: true, reports: true,
    users: true, settings: true,
  },
  admin: {
    income_types: true, expense_types: true, bank_accounts: true,
    record_income: true, payments: true, cash_book: true, reports: true,
    users: true, settings: true,
  },
  accountant: {
    income_types: true, expense_types: true, bank_accounts: true,
    record_income: true, payments: true, cash_book: true, reports: true,
    users: false, settings: false,
  },
  viewer: {
    income_types: false, expense_types: false, bank_accounts: false,
    record_income: false, payments: false, cash_book: true, reports: true,
    users: false, settings: false,
  },
};

const EMPTY_FORM = {
  id: null, username: '', password: '', confirm: '',
  full_name: '', email: '', role: 'accountant', status: 'active',
  permissions: { ...DEFAULT_PERMS_BY_ROLE.accountant },
};

export default function UserManagement() {
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const me = getUser();
  const isSuper = me?.role === 'superadmin';
  const ROLES = isSuper ? ['superadmin', 'admin', 'accountant', 'viewer'] : ['admin', 'accountant', 'viewer'];

  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(usersURL, { headers: headers() });
      if (res.data.success) setUsers(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(false);
    setForm({ ...EMPTY_FORM, permissions: { ...DEFAULT_PERMS_BY_ROLE.accountant } });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(true);
    setForm({
      id: u.id, username: u.username, password: '', confirm: '',
      full_name: u.full_name, email: u.email || '',
      role: u.role, status: u.status,
      permissions: { ...DEFAULT_PERMS_BY_ROLE[u.role], ...(u.permissions || {}) },
    });
    setError('');
    setDialogOpen(true);
  };

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const changeRole = (e) => {
    const role = e.target.value;
    setForm(f => ({ ...f, role, permissions: { ...DEFAULT_PERMS_BY_ROLE[role] } }));
  };

  const togglePerm = (key) => {
    if (form.role === 'superadmin') return;
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  };

  const setAllPerms = (val) => {
    if (form.role === 'superadmin') return;
    const next = {};
    PERMISSION_GROUPS.forEach(g => g.modules.forEach(m => { next[m.key] = val; }));
    next.dashboard = true;
    setForm(f => ({ ...f, permissions: next }));
  };

  const save = async () => {
    setError('');
    if (!form.full_name.trim() || !form.username.trim()) {
      setError('Full name and username are required.'); return;
    }
    if (!editing) {
      if (!form.password) { setError('Password is required.'); return; }
      if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
      if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    } else if (form.password && form.password !== form.confirm) {
      setError('Passwords do not match.'); return;
    }

    setSaving(true);
    try {
      if (editing) {
        const body = {
          id: form.id, full_name: form.full_name, email: form.email,
          role: form.role, status: form.status, permissions: form.permissions,
        };
        if (form.password) body.password = form.password;
        await axios.post(usersUpdateURL, body, { headers: headers() });
      } else {
        await axios.post(usersAddURL, {
          username: form.username, password: form.password,
          full_name: form.full_name, email: form.email, role: form.role,
          permissions: form.permissions,
        }, { headers: headers() });
      }
      setDialogOpen(false);
      load();
      notify.success(
        editing ? `${form.full_name} has been updated.` : `${form.full_name} can now sign in.`,
        { title: editing ? 'User updated' : 'User created' }
      );
    } catch (err) {
      const msg = err.response?.data?.message || 'Save failed.';
      setError(msg);
      notify.error(msg, { title: 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (u.id === me.id) return;
    const ok = await notify.confirm({
      title: 'Delete user?',
      message: `${u.full_name} will lose access to the system. This cannot be undone.`,
      confirmLabel: 'Delete user',
      severity: 'danger',
    });
    if (!ok) return;
    try {
      await axios.post(usersDeleteURL, { id: u.id }, { headers: headers() });
      load();
      notify.success(`${u.full_name} has been removed.`, { title: 'User deleted' });
    } catch (err) {
      notify.error(err.response?.data?.message || 'Delete failed.', { title: 'Delete failed' });
    }
  };

  const filtered = users.filter(u =>
    !search || `${u.full_name} ${u.username} ${u.email || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = users.filter(u => u.status === 'active').length;
  const adminCount  = users.filter(u => ['superadmin','admin'].includes(u.role)).length;

  const isSuperRow = (u) => u.role === 'superadmin';

  const grantedCount = PERMISSION_GROUPS.reduce((n, g) =>
    n + g.modules.filter(m => form.permissions?.[m.key]).length, 0);
  const totalCount = PERMISSION_GROUPS.reduce((n, g) => n + g.modules.length, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'stretch', sm: 'flex-end' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        mb: { xs: 2, sm: 3 }, gap: 2,
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="secondary.main" fontWeight={700} sx={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Team
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em', mt: 0.5 }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create accounts and control what each user can see.
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<Add />} onClick={openAdd}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
          sx={{ px: 3, flexShrink: 0 }}
        >
          Add User
        </Button>
      </Box>

      {/* Stat strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total users',  value: users.length,  icon: <ManageAccounts />, color: 'primary.main' },
          { label: 'Active',       value: activeCount,   icon: <VerifiedUser />,   color: 'success.main' },
          { label: 'Administrators', value: adminCount,  icon: <ShieldOutlined />, color: 'secondary.main' },
        ].map(s => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 42, height: 42, borderRadius: 1.5,
                  bgcolor: s.color, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {s.icon}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, fontSize: 10 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>{s.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <Box sx={{
          p: { xs: 1.5, sm: 2 },
          display: 'flex', alignItems: 'center',
          gap: 1.5, flexWrap: 'wrap',
        }}>
          <TextField
            placeholder="Search by name, username, email…"
            value={search} onChange={e => setSearch(e.target.value)} size="small"
            sx={{ flex: 1, minWidth: 200, maxWidth: 380 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
            {filtered.length} / {users.length}
          </Typography>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Access</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users found.</TableCell></TableRow>
                )}
                {filtered.map(u => {
                  const grants = Object.values(u.permissions || {}).filter(Boolean).length;
                  const total  = Object.keys(u.permissions || {}).length || 10;
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main', fontWeight: 700 }}>
                            {u.full_name?.[0] || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{u.full_name}</Typography>
                            {u.id === me.id && (
                              <Typography variant="caption" color="secondary.main" fontWeight={700}>YOU</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{u.username}</TableCell>
                      <TableCell>{u.email || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={u.role} color={ROLE_COLORS[u.role]} sx={{ textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell>
                        {isSuperRow(u) ? (
                          <Chip size="small" label="Full access" color="warning" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {grants}/{total} modules
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={u.status}
                          color={u.status === 'active' ? 'success' : 'default'}
                          variant={u.status === 'active' ? 'filled' : 'outlined'} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit user">
                          <IconButton size="small" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={u.id === me.id ? "Can't delete yourself" : 'Delete user'}>
                          <span>
                            <IconButton size="small" onClick={() => remove(u)} disabled={u.id === me.id} color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* ─── Dialog ───────────────────────────────────────────── */}
      <Dialog
        open={dialogOpen} onClose={() => setDialogOpen(false)}
        maxWidth="md" fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 },
          background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff',
          py: { xs: 2, sm: 2.5 }, px: { xs: 2, sm: 3 },
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5, bgcolor: '#C89211',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {editing ? <Edit /> : <Add />}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {editing ? 'Edit User' : 'Create New User'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {editing ? 'Update credentials, role, and access.' : 'Set credentials and choose what this user can access.'}
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {error && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            </Box>
          )}

          {/* Section: Details */}
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
              Account Details
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth required label="Full Name" name="full_name"
                  value={form.full_name} onChange={change} size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Email" name="email" type="email"
                  value={form.email} onChange={change} size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth required label="Username" name="username"
                  value={form.username} onChange={change} size="small" disabled={editing}
                  helperText={editing ? 'Username cannot be changed' : ' '}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField select fullWidth label="Role" name="role"
                  value={form.role} onChange={changeRole} size="small">
                  {ROLES.map(r => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField select fullWidth label="Status" name="status"
                  value={form.status} onChange={change} size="small" disabled={!editing}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth type="password" name="password"
                  label={editing ? 'New Password (leave blank to keep)' : 'Password'}
                  value={form.password} onChange={change} size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Lock sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth type="password" name="confirm" label="Confirm Password"
                  value={form.confirm} onChange={change} size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><KeyOutlined sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Section: Access */}
          <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FAFBFC' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
                  Access Permissions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.role === 'superadmin'
                    ? 'Superadmin has unrestricted access to every module.'
                    : `${grantedCount} of ${totalCount} modules granted.`}
                </Typography>
              </Box>
              {form.role !== 'superadmin' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setAllPerms(true)}>Select all</Button>
                  <Button size="small" variant="text" onClick={() => setAllPerms(false)}>Clear</Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={2}>
              {PERMISSION_GROUPS.map(group => (
                <Grid item xs={12} sm={6} key={group.title}>
                  <Paper variant="outlined" sx={{ p: 2, borderColor: '#E2E8F0', height: '100%' }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10 }}>
                      {group.title}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {group.modules.map(m => {
                        const on = form.role === 'superadmin' ? true : !!form.permissions?.[m.key];
                        return (
                          <Box
                            key={m.key}
                            onClick={() => togglePerm(m.key)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1.5,
                              py: 1, borderBottom: '1px solid #F1F5F9',
                              cursor: form.role === 'superadmin' ? 'not-allowed' : 'pointer',
                              opacity: form.role === 'superadmin' ? 0.7 : 1,
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': form.role === 'superadmin' ? {} : { bgcolor: 'rgba(11,37,69,0.03)' },
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>{m.label}</Typography>
                              <Typography variant="caption" color="text.secondary">{m.desc}</Typography>
                            </Box>
                            <Switch
                              checked={on}
                              disabled={form.role === 'superadmin'}
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => togglePerm(m.key)}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FAFBFC' }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ px: 3 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : (editing ? 'Save Changes' : 'Create User')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
